import { useQuery } from '@tanstack/react-query';
import { ApiError, api } from './api';
import { displayWorkspaceFileName } from './artifacts';
import { useAuth } from './auth';

export type FileEntry = {
  name: string;
  isDirectory: boolean;
  size: number;
  mtime: number;
};

export type FileListing = {
  path: string;
  items: FileEntry[];
};

export type UploadedFile = {
  name: string;
  size: number;
  path: string;
};

export const fileKeys = {
  user: (userId: number) => ['files', userId] as const,
  listing: (path: string, userId: number) => ['files', userId, path] as const,
};

export function useFileListing(path: string) {
  const { user } = useAuth();
  return useQuery({
    queryKey: user ? fileKeys.listing(path, user.id) : ['files', 'nil'],
    enabled: user != null,
    queryFn: async () => {
      const q = path ? `?path=${encodeURIComponent(path)}` : '';
      return api<FileListing>(`/files${q}`);
    },
  });
}

/**
 * Multipart upload — the server decides the final filename and
 * returns a list of saved entries with paths relative to the user's
 * workspace root.
 */
export async function uploadWorkspaceFiles(files: File[]): Promise<UploadedFile[]> {
  if (files.length === 0) return [];
  const form = new FormData();
  for (const f of files) form.append('files', f, f.name);
  const res = await fetch('/api/files/upload', {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  if (!res.ok) {
    let message = `Не удалось загрузить файл: HTTP ${res.status}`;
    try {
      const j = (await res.json()) as { error?: string };
      if (j.error) message = j.error;
    } catch {
      /* plain text */
    }
    throw new ApiError(res.status, message);
  }
  const data = (await res.json()) as { files: UploadedFile[] };
  return data.files;
}

/**
 * Fetch a preview of a workspace file (text / PDF / Office-as-PDF).
 * The caller renders text directly or opens a
 * Blob URL for a document viewer. Cookie auth rides on credentials.
 */
export async function fetchPreviewBlob(
  path: string,
  signal?: AbortSignal,
): Promise<{ blob: Blob; type: string }> {
  const res = await fetch(`/api/files/preview?path=${encodeURIComponent(path)}`, {
    credentials: 'include',
    signal,
  });
  if (!res.ok) {
    let message = `Не удалось открыть предпросмотр: HTTP ${res.status}`;
    try {
      const j = (await res.json()) as { error?: string };
      if (j.error) message = j.error;
    } catch {
      /* non-JSON */
    }
    throw new ApiError(res.status, message);
  }
  const blob = await res.blob();
  return { blob, type: res.headers.get('Content-Type') ?? blob.type };
}

export function previewMimeKind(type: string): 'text' | 'html' | 'pdf' | null {
  if (/^text\/plain\b/i.test(type)) return 'text';
  if (/^text\/html\b/i.test(type)) return 'html';
  if (/^application\/pdf\b/i.test(type)) return 'pdf';
  return null;
}

/**
 * Download a workspace file via cookie-authenticated fetch + blob,
 * then trigger a synthetic <a download> click. A bare `<a href>`
 * would also work (cookie rides on same-origin navigation) but
 * fetch keeps error handling inline with the rest of the module.
 */
export async function downloadWorkspaceFile(path: string): Promise<void> {
  const res = await fetch(`/api/files/download?path=${encodeURIComponent(path)}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const text = await res.text();
    let message = `Не удалось скачать файл: HTTP ${res.status}`;
    try {
      const j = JSON.parse(text) as { error?: string };
      if (j.error) message = j.error;
    } catch {
      /* plain text */
    }
    throw new ApiError(res.status, message);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = displayWorkspaceFileName(path);
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
